import { useEffect } from 'react';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { CameraId, SiteId } from '@cguard/types';
import { cguardFetcher } from '../runtime/fetcher';
import { normaliseCameraSummary, type BrandedCameraSummary } from './cameras';

interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

export interface NvrProbeInput {
  siteId: SiteId;
  address: string;
  username: string;
  password: string;
}

export interface NvrProbeDevice {
  vendor: string;
  model: string;
  address: string;
}

export interface NvrCameraCandidate {
  key: string;
  name: string;
  channelNumber: number;
  resolution: string;
  codec: string;
  recording: boolean;
  playbackAvailable: boolean;
}

export interface NvrProbeResult {
  sessionId: string;
  method: 'onvif' | 'hikvision-isapi' | 'onvif+hikvision-isapi';
  device: NvrProbeDevice;
  cameras: NvrCameraCandidate[];
  expiresAt: string;
}

export interface ImportNvrCamerasInput {
  siteId: SiteId;
  sessionId: string;
  cameraKeys: string[];
}

export interface ManualCameraInput {
  idempotencyKey: string;
  siteId: SiteId;
  name: string;
  ipAddress: string;
  mainStreamUrl: string;
  subStreamUrl?: string;
  playbackUrlTemplate?: string;
  resolutionMain: string;
  resolutionSub?: string;
  frameRateMain: number;
  frameRateSub?: number;
  codecMain: 'H264' | 'H265' | 'Av1';
  codecSub?: 'H264' | 'H265' | 'Av1';
}

export function serialiseManualCameraInput(input: ManualCameraInput): string {
  return JSON.stringify({
    siteId: input.siteId,
    nvrId: null,
    channelNumber: null,
    vendor: 'Generic',
    model: input.name,
    macAddress: null,
    ipAddress: input.ipAddress,
    rtspMainStreamUrl: input.mainStreamUrl,
    rtspSubStreamUrl: emptyToNull(input.subStreamUrl),
    playbackUrlTemplate: emptyToNull(input.playbackUrlTemplate),
    resolutionMain: input.resolutionMain,
    resolutionSub: emptyToNull(input.resolutionSub),
    frameRateMain: input.frameRateMain,
    frameRateSub: input.frameRateSub ?? null,
    codecMain: input.codecMain,
    codecSub: input.codecSub ?? null,
    aiArmedZones: [],
    tier: 'TierC',
    idempotencyKey: input.idempotencyKey,
  });
}

export type CameraAnalyticsCapability =
  | 'TierC'
  | 'TierMotion'
  | 'TierB'
  | 'TierLpr'
  | 'TierA'
  | 'TierAnomaly';

export interface CameraPlaybackInput {
  cameraId: CameraId;
  from: string;
  to: string;
}

export interface CameraPlaybackSession {
  url: string;
  expiresAt: string;
  format: 'fragmented-mp4';
}

export function useProbeNvr(): UseMutationResult<NvrProbeResult, Error, NvrProbeInput> {
  return useMutation({
    gcTime: 0,
    mutationFn: async (input) => {
      const response = await cguardFetcher<ApiResponse<unknown>>('/v1/cameras/onboarding/probe', {
        method: 'POST',
        body: serialiseNvrProbeInput(input),
      });
      return parseNvrProbeResult(response.data);
    },
  });
}

export function useImportNvrCameras(): UseMutationResult<
  BrandedCameraSummary[],
  Error,
  ImportNvrCamerasInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const response = await cguardFetcher<ApiResponse<unknown>>('/v1/cameras/onboarding/import', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      const body = asRecord(response.data, 'camera import response');
      const rows = body.cameras;
      if (!Array.isArray(rows)) throw new Error('Camera import response is missing cameras.');
      return rows.map((row) => normaliseCameraSummary(asRecord(row, 'imported camera')));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/v1/cameras'] });
    },
  });
}

export function useRegisterManualCamera(): UseMutationResult<
  BrandedCameraSummary,
  Error,
  ManualCameraInput
> {
  const queryClient = useQueryClient();
  const mutation = useMutation<BrandedCameraSummary, Error, ManualCameraInput>({
    gcTime: 0,
    mutationFn: async (input) => {
      const response = await cguardFetcher<ApiResponse<unknown>>('/v1/cameras/', {
        method: 'POST',
        body: serialiseManualCameraInput(input),
      });
      return normaliseCameraSummary(asRecord(response.data, 'registered camera'));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/v1/cameras'] });
    },
  });
  const { isError, isSuccess, reset } = mutation;

  useEffect(() => {
    if (isError || isSuccess) reset();
  }, [isError, isSuccess, reset]);

  return mutation;
}

export function useEnableCameras(): UseMutationResult<void, Error, readonly CameraId[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (cameraIds) => {
      await Promise.all(
        cameraIds.map((cameraId) =>
          cguardFetcher<ApiResponse<unknown>>(`/v1/cameras/${cameraId as string}/enable`, {
            method: 'POST',
          }),
        ),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/v1/cameras'] });
    },
  });
}

export function useSetCameraAnalytics(): UseMutationResult<
  void,
  Error,
  { cameraId: CameraId; capability: CameraAnalyticsCapability }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ cameraId, capability }) => {
      await cguardFetcher<ApiResponse<unknown>>(`/v1/cameras/${cameraId}/tier`, {
        method: 'POST',
        body: JSON.stringify({ tier: capability }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/v1/cameras'] });
    },
  });
}

export function useCreateCameraPlayback(): UseMutationResult<
  CameraPlaybackSession,
  Error,
  CameraPlaybackInput
> {
  return useMutation({
    mutationFn: async ({ cameraId, from, to }) => {
      const response = await cguardFetcher<ApiResponse<unknown>>(
        `/v1/cameras/${cameraId}/playback`,
        {
          method: 'POST',
          body: JSON.stringify({ from, to }),
        },
      );
      const body = asRecord(response.data, 'playback response');
      const url = requireString(body.url, 'playback URL');
      const expiresAt = requireString(body.expiresAt, 'playback expiry');
      if (body.format !== 'fragmented-mp4') {
        throw new Error('Playback response has an unsupported media format.');
      }
      return { url, expiresAt, format: 'fragmented-mp4' };
    },
  });
}

export function serialiseNvrProbeInput(input: NvrProbeInput): string {
  return JSON.stringify({
    siteId: input.siteId,
    address: input.address,
    username: input.username,
    password: input.password,
  });
}

export function parseNvrProbeResult(value: unknown): NvrProbeResult {
  const body = asRecord(value, 'NVR probe response');
  const method = body.method;
  if (method !== 'onvif' && method !== 'hikvision-isapi' && method !== 'onvif+hikvision-isapi') {
    throw new Error('NVR probe response has an unknown discovery method.');
  }
  const device = asRecord(body.device, 'NVR device');
  if (!Array.isArray(body.cameras)) {
    throw new Error('NVR probe response is missing camera candidates.');
  }
  return {
    sessionId: requireString(body.sessionId, 'discovery session'),
    method,
    device: {
      vendor: requireString(device.vendor, 'device vendor'),
      model: requireString(device.model, 'device model'),
      address: requireString(device.address, 'device address'),
    },
    cameras: body.cameras.map((candidateValue) => {
      const camera = asRecord(candidateValue, 'camera candidate');
      return {
        key: requireString(camera.key, 'camera key'),
        name: requireString(camera.name, 'camera name'),
        channelNumber: requireNumber(camera.channelNumber, 'channel number'),
        resolution: requireString(camera.resolution, 'camera resolution'),
        codec: requireString(camera.codec, 'camera codec'),
        recording: Boolean(camera.recording),
        playbackAvailable: Boolean(camera.playbackAvailable),
      };
    }),
    expiresAt: requireString(body.expiresAt, 'discovery expiry'),
  };
}

function asRecord(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label} is invalid.`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${label} is missing.`);
  }
  return value;
}

function requireNumber(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${label} is invalid.`);
  }
  return value;
}

function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}
